import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import { FileSpreadsheet, Loader2, RefreshCw, Search, User } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import SearchableAgentSelect from "@/components/common/SearchableAgentSelect";
import { cn } from "@/lib/utils";
import {
  PanelTableCard,
  PanelTableHeader,
  PanelTableFrame,
  PanelTableHeadRow,
  PanelTableHead,
  PanelTableBody,
  PanelTableRow,
  PanelTableCell,
  PanelTableEmpty,
  PanelTablePagination,
  PanelTableFooter,
  usePanelTablePagination,
} from "@/components/ui/panel-table";

const MrExcelRecordsPage = ({
  apiBase,
  isDark,
  textClass,
  labelClass,
  inputClass,
  isSuperAdmin,
  userCompanyId,
  companies = [],
}) => {
  const [companyId, setCompanyId] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [search, setSearch] = useState("");
  const [agentFilterId, setAgentFilterId] = useState(null);
  const [companyAgents, setCompanyAgents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);

  const spnDefault = useMemo(
    () => companies.find((c) => String(c.kod || "").toLowerCase() === "spn" && c.status === 1)?.id,
    [companies],
  );

  const effectiveCompanyId = useMemo(() => {
    if (isSuperAdmin) {
      const n = Number(companyId);
      return companyId && Number.isFinite(n) && n > 0 ? n : null;
    }
    const n = Number(userCompanyId);
    return Number.isFinite(n) && n > 0 ? n : null;
  }, [isSuperAdmin, companyId, userCompanyId]);

  useEffect(() => {
    if (!isSuperAdmin || companyId || !spnDefault) return;
    setCompanyId(String(spnDefault));
  }, [isSuperAdmin, companyId, spnDefault]);

  useEffect(() => {
    setAgentFilterId(null);
    setData(null);
  }, [effectiveCompanyId]);

  useEffect(() => {
    if (!effectiveCompanyId) {
      setCompanyAgents([]);
      return;
    }
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get(`${apiBase}/agents`, { timeout: 90000 });
        const raw = Array.isArray(res.data) ? res.data : (Array.isArray(res.data?.data) ? res.data.data : []);
        const list = raw.filter((a) => Number(a.company_id) === Number(effectiveCompanyId));
        if (!cancelled) setCompanyAgents(list);
      } catch {
        if (!cancelled) setCompanyAgents([]);
      }
    })();
    return () => { cancelled = true; };
  }, [apiBase, effectiveCompanyId]);

  const load = useCallback(async (options = {}) => {
    if (!effectiveCompanyId) {
      toast.warning("Firma seçin");
      return;
    }
    const resolvedAgent = options.agentId !== undefined ? options.agentId : agentFilterId;
    setLoading(true);
    try {
      const res = await axios.get(`${apiBase}/mr-excel-records`, {
        params: {
          company_id: effectiveCompanyId,
          start_date: startDate || undefined,
          end_date: endDate || undefined,
          search: search.trim() || undefined,
          agent_id: resolvedAgent ? Number(resolvedAgent) : undefined,
          limit: 500,
          page: 1,
        },
        timeout: 120000,
      });
      setData(res.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "MR kayıtları yüklenemedi");
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [apiBase, effectiveCompanyId, startDate, endDate, search, agentFilterId]);

  useEffect(() => {
    if (effectiveCompanyId) load();
  }, [effectiveCompanyId, load]);

  const items = Array.isArray(data?.items) ? data.items : [];
  const pagination = usePanelTablePagination(items, {
    pageSize: 25,
    resetDeps: [data?.total, agentFilterId, startDate, endDate, search],
  });
  const pageRows = pagination.paginatedItems ?? [];

  const summaryAll = data?.summary_all;
  const summaryFiltered = data?.summary_filtered;
  const byAgent = Array.isArray(data?.by_agent) ? data.by_agent : [];
  const agentFilterActive = Boolean(agentFilterId);

  const selectedAgent = useMemo(() => {
    if (!agentFilterId) return null;
    return companyAgents.find((a) => Number(a.id) === Number(agentFilterId)) ?? null;
  }, [agentFilterId, companyAgents]);

  const selectedAgentName = useMemo(() => {
    if (!agentFilterId) return null;
    if (selectedAgent?.ad_soyad) return selectedAgent.ad_soyad;
    const fromSummary = byAgent.find((r) => Number(r.agent_id) === Number(agentFilterId));
    return fromSummary?.agent_name ?? null;
  }, [agentFilterId, selectedAgent, byAgent]);

  const applyAgentFilter = (agent) => {
    setAgentFilterId(agent?.id != null ? Number(agent.id) : null);
  };

  const summaryBlockClass = isDark
    ? "border-slate-700/80 bg-slate-900/50"
    : "border-gray-200 bg-gray-50/90";

  return (
    <PanelTableCard isDark={isDark}>
      <PanelTableHeader
        isDark={isDark}
        textClass={textClass}
        labelClass={labelClass}
        accent="purple"
        icon={FileSpreadsheet}
        title="MR Kayıtları (Excel)"
        description="Excel import ile kaydedilen günlük MR sayıları — ad soyada göre agent eşleşmesi, Firebase kullanılmaz."
        stackToolbar
        toolbar={(
          <div className="flex flex-col gap-3 w-full">
            <div className={`flex flex-wrap items-end gap-2 rounded-xl border px-3 py-2.5 ${isDark ? "border-slate-700/80 bg-slate-900/40" : "border-gray-200 bg-white/80"}`}>
              {isSuperAdmin && (
                <div className="w-full sm:w-44">
                  <label className={`text-[10px] ${labelClass}`}>Firma</label>
                  <Select value={companyId || "none"} onValueChange={(v) => setCompanyId(v === "none" ? "" : v)}>
                    <SelectTrigger className={`h-9 mt-0.5 text-sm ${inputClass}`}>
                      <SelectValue placeholder="Firma" />
                    </SelectTrigger>
                    <SelectContent className={isDark ? "bg-slate-900 border-slate-800" : ""}>
                      <SelectItem value="none">Seçin</SelectItem>
                      {companies.filter((c) => c.status === 1).map((c) => (
                        <SelectItem key={c.id} value={String(c.id)}>{c.ad}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
              <div className="w-full sm:w-52">
                <label className={`text-[10px] ${labelClass}`}>Agent</label>
                <SearchableAgentSelect
                  agents={companyAgents}
                  value={selectedAgent}
                  onChange={applyAgentFilter}
                  isDark={isDark}
                  inputClass={inputClass}
                  labelClass={labelClass}
                  placeholder="Tüm agentlar"
                  nameOnly
                  className="mt-0.5"
                  size="sm"
                />
              </div>
              <div>
                <label className={`text-[10px] ${labelClass}`}>Başlangıç</label>
                <Input type="date" className={`h-9 mt-0.5 w-36 ${inputClass}`} value={startDate} onChange={(e) => setStartDate(e.target.value)} />
              </div>
              <div>
                <label className={`text-[10px] ${labelClass}`}>Bitiş</label>
                <Input type="date" className={`h-9 mt-0.5 w-36 ${inputClass}`} value={endDate} onChange={(e) => setEndDate(e.target.value)} />
              </div>
              <div className="flex-1 min-w-[160px]">
                <label className={`text-[10px] ${labelClass}`}>Asistan ara</label>
                <div className="relative mt-0.5">
                  <Search className={`absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 ${labelClass}`} />
                  <Input className={`h-9 pl-8 ${inputClass}`} value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Ad soyad…" />
                </div>
              </div>
              <Button type="button" size="sm" className="h-9" disabled={loading} onClick={load}>
                {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4 mr-1" />}
                Listele
              </Button>
            </div>
          </div>
        )}
      />
      <PanelTableFrame
        isDark={isDark}
        footer={items.length > 0 ? (
          <PanelTablePagination
            isDark={isDark}
            page={pagination.page}
            pageSize={pagination.pageSize}
            total={pagination.total}
            totalPages={pagination.totalPages}
            rangeStart={pagination.rangeStart}
            rangeEnd={pagination.rangeEnd}
            onPageChange={pagination.setPage}
          />
        ) : null}
      >
        <table className="w-full text-sm">
          <PanelTableHeadRow isDark={isDark}>
            <PanelTableHead isDark={isDark}>Tarih</PanelTableHead>
            <PanelTableHead isDark={isDark}>Excel adı</PanelTableHead>
            <PanelTableHead isDark={isDark}>Panel agent</PanelTableHead>
            <PanelTableHead isDark={isDark} className="text-right">MR adet</PanelTableHead>
            <PanelTableHead isDark={isDark}>Kaydedildi</PanelTableHead>
          </PanelTableHeadRow>
          <PanelTableBody>
            {loading && (
              <PanelTableRow isDark={isDark}>
                <PanelTableCell colSpan={5} className="text-center py-8">
                  <Loader2 className={`w-6 h-6 animate-spin mx-auto ${isDark ? "text-violet-400" : "text-violet-600"}`} />
                </PanelTableCell>
              </PanelTableRow>
            )}
            {!loading && pageRows.length === 0 && (
              <PanelTableEmpty colSpan={5} isDark={isDark} title="Kayıt yok" description="MR Excel import yaptıktan sonra burada görünür." />
            )}
            {!loading && pageRows.map((row) => (
              <PanelTableRow key={row.id} isDark={isDark}>
                <PanelTableCell className="tabular-nums whitespace-nowrap">{row.report_date}</PanelTableCell>
                <PanelTableCell>{row.assistant_name}</PanelTableCell>
                <PanelTableCell>
                  {row.agent_name || (row.agent_id ? `#${row.agent_id}` : "—")}
                </PanelTableCell>
                <PanelTableCell className="text-right tabular-nums font-medium">{row.net_count}</PanelTableCell>
                <PanelTableCell className={`text-xs ${labelClass} whitespace-nowrap`}>
                  {row.synced_at ? new Date(row.synced_at).toLocaleString("tr-TR") : "—"}
                </PanelTableCell>
              </PanelTableRow>
            ))}
          </PanelTableBody>
        </table>
      </PanelTableFrame>

      {data && !loading && (
        <div className={`border-t ${isDark ? "border-slate-800" : "border-gray-100"}`}>
          <PanelTableFooter isDark={isDark} className="flex-col items-stretch gap-3">
            <div className="grid gap-2 sm:grid-cols-2 w-full">
              <div className={`rounded-lg border px-3 py-2.5 ${summaryBlockClass}`}>
                <p className={`text-[10px] uppercase tracking-wide font-semibold mb-1 ${labelClass}`}>
                  Tüm kayıtlar (tarih / arama)
                </p>
                <p className={`text-sm ${textClass}`}>
                  <strong>{summaryAll?.record_count ?? 0}</strong> günlük kayıt
                  {" · "}
                  <strong>{summaryAll?.net_total ?? 0}</strong> MR
                  {" · "}
                  <strong>{summaryAll?.agent_count ?? 0}</strong> agent
                </p>
              </div>
              <div className={`rounded-lg border px-3 py-2.5 ${summaryBlockClass}`}>
                <p className={`text-[10px] uppercase tracking-wide font-semibold mb-1 flex items-center gap-1 ${labelClass}`}>
                  <User className="w-3 h-3" />
                  {agentFilterActive ? "Seçili agent" : "Liste filtresi (tablo)"}
                </p>
                <p className={`text-sm ${textClass}`}>
                  {agentFilterActive && selectedAgentName && (
                    <span className="font-medium">{selectedAgentName} — </span>
                  )}
                  <strong>{summaryFiltered?.record_count ?? data.total ?? 0}</strong> kayıt
                  {" · "}
                  <strong>{summaryFiltered?.net_total ?? data.net_total ?? 0}</strong> MR
                </p>
                {agentFilterActive && (
                  <Button
                    type="button"
                    variant="link"
                    className="h-auto p-0 text-[11px] mt-0.5"
                    onClick={() => {
                      setAgentFilterId(null);
                      load({ agentId: null });
                    }}
                  >
                    Agent filtresini kaldır
                  </Button>
                )}
              </div>
            </div>

            {byAgent.length > 0 && (
              <div className="w-full">
                <p className={`text-[10px] uppercase tracking-wide font-semibold mb-2 ${labelClass}`}>
                  Agent bazlı toplamlar (satıra tıklayarak filtreleyin)
                </p>
                <div className={`rounded-lg border overflow-hidden max-h-56 overflow-y-auto ${isDark ? "border-slate-700" : "border-gray-200"}`}>
                  <table className="w-full text-xs">
                    <thead className={isDark ? "bg-slate-800/80 sticky top-0" : "bg-gray-100 sticky top-0"}>
                      <tr>
                        <th className={`text-left px-3 py-2 font-semibold ${labelClass}`}>Agent</th>
                        <th className={`text-right px-3 py-2 font-semibold ${labelClass}`}>Gün kaydı</th>
                        <th className={`text-right px-3 py-2 font-semibold ${labelClass}`}>Toplam MR</th>
                      </tr>
                    </thead>
                    <tbody>
                      {byAgent.map((row) => {
                        const id = row.agent_id;
                        const active = id && Number(id) === Number(agentFilterId);
                        return (
                          <tr
                            key={`${id ?? "x"}-${row.agent_name}`}
                            role={id ? "button" : undefined}
                            tabIndex={id ? 0 : undefined}
                            className={cn(
                              "border-t transition-colors",
                              isDark ? "border-slate-800" : "border-gray-100",
                              id && "cursor-pointer hover:bg-violet-500/10",
                              active && (isDark ? "bg-violet-950/40" : "bg-violet-50"),
                            )}
                            onClick={() => {
                              if (!id) return;
                              const numId = Number(id);
                              setAgentFilterId(numId);
                              load({ agentId: numId });
                            }}
                            onKeyDown={(e) => {
                              if (!id || (e.key !== "Enter" && e.key !== " ")) return;
                              e.preventDefault();
                              const numId = Number(id);
                              setAgentFilterId(numId);
                              load({ agentId: numId });
                            }}
                          >
                            <td className={`px-3 py-1.5 ${textClass}`}>{row.agent_name}</td>
                            <td className={`px-3 py-1.5 text-right tabular-nums ${textClass}`}>{row.record_count}</td>
                            <td className={`px-3 py-1.5 text-right tabular-nums font-medium ${textClass}`}>{row.net_total}</td>
                          </tr>
                        );
                      })}
                    </tbody>
                    <tfoot className={isDark ? "bg-slate-900/60 border-t border-slate-700" : "bg-gray-50 border-t border-gray-200"}>
                      <tr>
                        <td className={`px-3 py-2 font-semibold ${textClass}`}>Genel toplam</td>
                        <td className={`px-3 py-2 text-right tabular-nums font-semibold ${textClass}`}>
                          {summaryAll?.record_count ?? 0}
                        </td>
                        <td className={`px-3 py-2 text-right tabular-nums font-semibold ${textClass}`}>
                          {summaryAll?.net_total ?? 0}
                        </td>
                      </tr>
                    </tfoot>
                  </table>
                </div>
              </div>
            )}
          </PanelTableFooter>
        </div>
      )}
    </PanelTableCard>
  );
};

export default MrExcelRecordsPage;
